import React from 'react';
import { useAuth } from '../contexts/AuthContext';

import devLog from '../utils/devLog';

/**
 * V25.0.3: Session Health - SILENT AUTO-REFRESH
 * No popup/banner shown - automatically refreshes session in background
 * User interaction (click, mousemove) triggers auto-refresh if needed
 */
export const SessionHealthBanner = () => {
  const { showSessionBanner, manualRefreshSession } = useAuth();
  const refreshingRef = React.useRef(false);
  const hasAutoRefreshedRef = React.useRef(false);

  // V25.0.3: Silent auto-refresh when session issue detected
  React.useEffect(() => {
    if (showSessionBanner && !hasAutoRefreshedRef.current && !refreshingRef.current) {
      devLog.log('[SessionHealth V25.0.3] Session issue detected - auto-refreshing silently');
      hasAutoRefreshedRef.current = true;
      refreshingRef.current = true;
      
      // Silent refresh - no UI shown
      manualRefreshSession().then(() => {
        devLog.log('[SessionHealth V25.0.3] Silent auto-refresh complete');
        refreshingRef.current = false;
      }).catch(err => {
        devLog.warn('[SessionHealth V25.0.3] Silent auto-refresh failed:', err);
        refreshingRef.current = false;
      });
    }
  }, [showSessionBanner, manualRefreshSession]);

  // V25.0.3: User interaction triggers refresh if session is stale
  React.useEffect(() => {
    const handleUserActivity = () => {
      if (showSessionBanner && !refreshingRef.current) {
        devLog.log('[SessionHealth V25.0.3] User activity detected - refreshing session');
        refreshingRef.current = true;
        manualRefreshSession().then(() => {
          refreshingRef.current = false;
        }).catch(() => {
          refreshingRef.current = false;
        });
      }
    };

    // Listen for user interactions
    const events = ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'];
    
    // Debounce to prevent multiple rapid refreshes
    let debounceTimer = null;
    const debouncedHandler = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(handleUserActivity, 1000);
    };

    events.forEach(event => {
      window.addEventListener(event, debouncedHandler, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, debouncedHandler);
      });
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, [showSessionBanner, manualRefreshSession]);

  // V25.0.3: NO UI RENDERED - completely invisible to user
  return null;
};
